import React, { useEffect, useState } from 'react'
import Select from '../components/Input/select'
import {
  getDaysInMonth,
  getDefaultDaysList,
  getItalianRegions,
  getMonthsList,
  getYearsList
} from '../utils/date-utils'
import ExpandableContainer from '../components/ui/expandable-container'
import { getAmoreNumbersResponse } from '../services/backend-services'
import { AmoreFormData } from '../types/numeri-amore'
import { extractAmoreResponseDataFromJSON } from '../utils/extract-data-from-json'
import { SIGNAL_EVENT } from '../types/enums'
import SignalsService from '../services/communication/signals.service'
import PostMessageService, { PostMessageType } from '../services/communication/post-message.service'
import Button from '../components/ui/button'
import ErrorMessage from '../components/ui/error-message'
import Resizer from '../components/ui/resizer'
import Number from '../components/ui/number'

/** Pagina amore che permette la visualizzazione dei numeri consigliati da giocare quando tutte le select hanno un'opzione (diversa dalla stringa vuota) selezionata dall'utente
 *
 * @desc questa pagina non deve permettere il submit qual ora non tutte le select abbiano una risposta e quindi il bottone è disabilitato.
 * Le select su cui l'utente ha cliccato almeno una volta, senza selezionare una risposta, devono essere evidenziate con un bordo rosso.
 */
const SignificatiNumeriAmore = () => {
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const [numbers, setNumbers] = useState<string[]>([])
  const [showError, setShowError] = useState<boolean>(false)

  const [dataToSubmit, setDataToSubmit] = useState<AmoreFormData>({})

  // Se viene modificato l'anno o il mese, bisogna verificare se i giorni del mese selezionato siano diversi da quelli correnti
  const possibleDays = dataToSubmit.month
    ? getDaysInMonth(
        parseInt(dataToSubmit.month),
        dataToSubmit.year ? parseInt(dataToSubmit.year) : undefined
      ).map((num) => num.toString())
    : getDefaultDaysList()
  const possibleMonths = getMonthsList()
  const possibleYear = getYearsList()
  const isDefaultNone = true
  const showNumbers = numbers?.length > 0 // se sono stati trovati dei numeri, dovrò mostrarli
  const amoreTestId = 'amore'

  const handleOnDayChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDataToSubmit({ ...dataToSubmit, day: e.target.value })
  }

  const handleOnYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDataToSubmit({ ...dataToSubmit, year: e.target.value })
  }

  const handleOnMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDataToSubmit({ ...dataToSubmit, month: e.target.value })
  }

  const handleOnRegionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDataToSubmit({ ...dataToSubmit, region: e.target.value })
  }

  // quando i dati inseriti nelle select cambiano, verifica che ogni select abbia una risposta. Se sì, abilita il bottone per il submit
  useEffect(() => {
    setShowError(false)
    setNumbers([])
    const allFieldsPresent = ['day', 'month', 'year', 'region'].every(
      (key) =>
        dataToSubmit[key as keyof AmoreFormData] !== undefined &&
        dataToSubmit[key as keyof AmoreFormData] !== ''
    )

    setIsButtonDisabled(!allFieldsPresent)
  }, [dataToSubmit])

  const handleButtonClick = async () => {
    try {
      SignalsService.fire(SIGNAL_EVENT.showLoader, 'flex')
      const responseJson = await getAmoreNumbersResponse(dataToSubmit)
      const newNumbers = extractAmoreResponseDataFromJSON(responseJson)
      setNumbers(newNumbers)
    } catch (e) {
      if (e instanceof Error) {
        console.error('Significati numeri amore:', e.message)
        setNumbers([])
        setShowError(true)
      }
    } finally {
      SignalsService.fire(SIGNAL_EVENT.showLoader, 'hidden')
    }
  }

  useEffect(() => {
    if (numbers.length > 0) {
      PostMessageService.send({
        type: PostMessageType.PLAY_NUMBERS,
        numbers: numbers
      })
    } else {
      PostMessageService.send({
        type: PostMessageType.RESET_NUMBERS
      })
    }
  }, [numbers])

  return (
    <Resizer idResizer={amoreTestId}>
      <div className={'w-full h-full text-base bg-lightOrangeColor'}>
        <form>
          <div
            className={
              'py-5 grid desktop:grid-rows-1 desktop:grid-cols-[400px_1fr_1fr] table:grid-rows-2 mobile:grid-cols-1'
            }>
            <div className={'px-3.7'}>
              <div className={'flex flex-col'}>
                <div className={'px-3.7'}>
                  <p>Inserisci la data:</p>
                </div>
                <div className={'grid grid-cols-3 mobile:grid-cols-1'}>
                  <Select
                    options={possibleDays}
                    idSelect={'giorno'}
                    highlightError={true}
                    isDefaultNone={isDefaultNone}
                    onChange={handleOnDayChange}
                  />
                  <Select
                    options={possibleMonths}
                    idSelect={'mese'}
                    highlightError={true}
                    isDefaultNone={isDefaultNone}
                    onChange={handleOnMonthChange}
                  />
                  <Select
                    options={possibleYear}
                    idSelect={'anno'}
                    highlightError={true}
                    isDefaultNone={isDefaultNone}
                    onChange={handleOnYearChange}
                  />
                </div>
              </div>
            </div>
            <div className={'px-3.7'}>
              <div className={'w-full px-3.7'}>
                <p>Inserisci il luogo:</p>
              </div>
              <div className={'w-full'}>
                <Select
                  options={getItalianRegions()}
                  idSelect={'luogo'}
                  highlightError={true}
                  isDefaultNone={isDefaultNone}
                  onChange={handleOnRegionChange}
                />
              </div>
            </div>
            <div className={'px-3.7 w-full relative top-2.5 flex items-center h-20'}>
              <Button
                idButton={amoreTestId}
                buttonText={'calcola la tua cinquina'}
                disabled={isButtonDisabled}
                handleClick={handleButtonClick}
                className={`max-h-[54.8px] text-nowrap`}
              />
            </div>
          </div>
          <ExpandableContainer
            id={amoreTestId}
            expandableCondition={showNumbers}
            className={`transition-vertical-spacing duration-1000 ease-out overflow-hidden ${!showNumbers ? '' : 'py-5'}`}
            tot_padding_y={40}>
            <div className={'flex justify-center items-center px-3.7'}>
              <h2>ECCO LA CINQUINA DELL'AMORE</h2>
            </div>
            <div
              id={amoreTestId + '-numbers-container'}
              className={'px-3.7 flex justify-center mobile:w-full mobile:justify-between'}>
              {numbers?.map((numero, idx) => (
                <Number
                  key={`${amoreTestId}-${numero}-${idx}`}
                  idNumber={`${amoreTestId}-${idx}`}
                  numberTxt={numero}
                  customClassName={
                    'mx-1.5 mobile:mx-3.7 h-[50px] desktop:w-[50px] tablet:w-[40px] mobile:flex-grow'
                  }
                />
              ))}
            </div>
          </ExpandableContainer>
        </form>
      </div>
      <ErrorMessage idError={amoreTestId} isVisible={showError} />
    </Resizer>
  )
}

export default SignificatiNumeriAmore
